import { FormGenerateGCCModal } from 'components/Molecules/Forms/index';
import React from 'react';
import { Button, notification, Modal } from 'antd';
import { useOne } from 'context/ResourceProvider/hooks/useOne';
import get from 'lodash/get';
import { GccCpcMappings } from 'mappers/GccCpcMapper';
import isEmpty from 'lodash/isEmpty';
import { useCreate } from 'context/ResourceProvider/hooks/useCreate';
import { useUpdate } from 'context/ResourceProvider/hooks/useUpdate';
import { useList } from 'context/ResourceProvider/hooks/useList';
import { transformResponse } from 'helpers/api';
import moment from 'moment';
import { WithModal } from 'hocs/withModal';
import PreviewDocument from 'components/Molecules/PreviewDocument/index';
import { INITIAL_VALUES, QMASDocumentModal } from 'components/Molecules/Upload/QMASDocument/index';
import { useMe } from 'modules/company/hooks/useCompanySelector';
import useUploadFile from 'modules/inspection/hooks/useUploadDocument';
import { ComplianceMappings } from 'mappers/ComplianceDataMapper';
import useDownloadFile from 'hooks/useDownloadFile';
import PropTypes from 'prop-types';
import SignOffForm from 'components/Molecules/Forms/SignOff';
import { FileHelpers } from 'helpers/file/index';
import classes from '../GenerateForm.module.scss';

const PreviewDocumentModal = WithModal(PreviewDocument);
const SignOffFormModal = WithModal(SignOffForm);

const MAPPER_BY_TYPE = {
  gcc: GccCpcMappings.parseGCCFormFromAPI,
  cpc: GccCpcMappings.parseGCCFormFromAPI,
  doc: GccCpcMappings.parseDoCFormFromAPI,
  signOff: GccCpcMappings.parseSignOffFromAPI,
};

const TRANSFORMER_BY_TYPE = {
  gcc: GccCpcMappings.transformGCCFormToAPI,
  cpc: GccCpcMappings.transformGCCFormToAPI,
  doc: GccCpcMappings.transformDoCFormToAPI,
  signOff: GccCpcMappings.transformSignOffToAPI,
};

/**
 * The GCCForm Modal is applicable for GCC, CPC, and DOC
 * The workflow: create/view -> preview -> publish
 * @param {*} props
 * @returns
 */
const GCCFormModal = (props) => {
  const { company } = useMe();
  const { uploadFile } = useUploadFile();
  const [download] = useDownloadFile();
  const [modal, context] = Modal.useModal();
  const [pdfPreviewUrl, setPdfPreviewUrl] = React.useState(null);
  const [showPreview, setShowPreview] = React.useState(false);
  const [showSetReminder, setShowReminder] = React.useState(false);
  const [currentGCC, setCurrentGCC] = React.useState(null);
  const {
    productId,
    countryId,
    product,
    modalForm,
    openForm,
    closeForm,
    onConfirmSuccess,
    type = 'gcc',
  } = props;
  const [errors, setErrors] = React.useState({});
  const [workFlow, setWorkFlow] = React.useState('create');
  const formRef = React.useRef();
  const reminderRef = React.useRef();
  const signOffRef = React.useRef();
  const exportType = React.useRef('');

  const { data: gccLatestDraft, refetch: refetchLatestDraft } = useList({
    resource: 'product-certificate-gcc-cpc-latest',
    meta: {
      transformResponse: [transformResponse],
      params: {
        type,
        productId,
      },
    },
  });

  const { data: gccDetail, refetch: refetchDetail } = useOne({
    resource: 'product-certificate-gcc-cpc',
    meta: {
      params: {
        productId,
        type,
      },
    },
    id: currentGCC,
    skip: !currentGCC,
  });

  const { mutate: updateGccDraft, loading: updateDraftLoading } = useUpdate({
    resource: 'product-certificate-gcc-cpc',
    meta: {
      params: {
        productId,
        type,
      },
    },
    id: currentGCC,
    skip: !currentGCC,
  });

  const { mutate: createGccDraft, loading: createDraftLoading } = useCreate({
    resource: 'product-certificate-gcc-cpc',
    meta: {
      params: {
        productId,
        type,
      },
    },
  });

  const { refetch: refetchPreview, loading: fetchPreviewFileLoading } = useList({
    resource: 'product-certificate-gcc-cpc-preview-publish',
    meta: {
      params: {
        productId,
        type,
        id: currentGCC,
      },
      responseType: 'arraybuffer',
    },
    skip: true,
  });

  const { data, loaded } = useOne({
    resource: 'company',
    id: get(product, 'companyId'),
    skip: !get(product, 'companyId'),
  });

  const { mutate: publish, loading: confirmDocumentLoading } = useCreate({
    resource: 'product-certificate-gcc-cpc-preview-publish',
    meta: {
      params: {
        type,
        productId,
        id: currentGCC,
      },
    },
  });

  const latestData = React.useMemo(() => {
    const draft = get(gccLatestDraft, 'data', null);
    if (workFlow === 'create') {
      return draft;
    }
    return gccDetail;
  }, [gccLatestDraft, gccDetail, type, workFlow]);

  const lastSavedData = React.useMemo(() => {
    const updated = get(latestData || {}, 'updatedAt', null);
    if (!updated) return null;
    return moment(updated).format('HH:mm, DD/MM/YYYY');
  }, [latestData]);

  const onFormErrors = (formErrors) => {
    setErrors(formErrors);
  };

  const getMessageError = () => {
    if (!errors) return null;
    const sectionInCompleted = [];
    Object.entries(errors).forEach(([key]) => {
      if (key === 'general') {
        sectionInCompleted.push(`"General Information"`);
      }
      if (key === 'testData') {
        sectionInCompleted.push(`"Test Data"`);
      }
      if (key === 'laboratory') {
        sectionInCompleted.push(`"Laboratory Information"`);
      }
    });
    const lastSection = sectionInCompleted.pop();
    if (sectionInCompleted.length === 0) return `The ${lastSection} section is incomplete`;
    return `The ${sectionInCompleted.join(', ')} and ${lastSection} sections are incomplete`;
  };

  const resetFlow = () => {
    setWorkFlow('create');
    setCurrentGCC(null);
    setShowPreview(false);
    setShowReminder(false);
    refetchLatestDraft();
  };

  const handleNextStep = async () => {
    try {
      if (workFlow === 'preview') {
        closeForm(type);
        setShowPreview(true);
        const previewFile = await refetchPreview({
          meta: {
            params: {
              id: currentGCC,
            },
          },
        });
        if (!previewFile.error) {
          const arrayBuffer = new Uint8Array(previewFile.data);
          setPdfPreviewUrl(arrayBuffer);
        } else {
          notification.error({
            message: 'Load Preview Failed',
          });
        }
      }
      if (workFlow === 'signOff') {
        // Doc
        // Show Modal Signatures
        closeForm(type);
        setWorkFlow('signOff');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpload = async (value) => {
    let currentUploadedFile = value.filter((file) => !file.originFileObj);
    const willUpload = value
      .filter((file) => file.originFileObj)
      .map(({ originFileObj }) => originFileObj);

    if (willUpload.length) {
      const uploadPromises = await uploadFile(willUpload, company.companyId);
      currentUploadedFile = currentUploadedFile.concat(uploadPromises);
    }
    return currentUploadedFile;
  };

  const handleSaveData = async (formValues, isLastStep) => {
    try {
      const { id, general, testData, laboratory, notifiedBodyInformation } = formValues;
      const { productPictures, companyLogo, ...restGeneral } = general;
      const uploadedProductPictures = await handleUpload(productPictures);
      let uploadedCompanyLogo = [];

      if (type === 'doc') {
        uploadedCompanyLogo = await handleUpload(companyLogo);
      }

      const payload = {
        id,
        ...restGeneral,
        companyLogo: uploadedCompanyLogo,
        productPictures: uploadedProductPictures,
        testData,
        laboratory,
        notifiedBodyInformation,
      };

      const transformer = TRANSFORMER_BY_TYPE[type];
      const transformed = transformer(payload);
      if (id) {
        const resUpdated = await updateGccDraft(transformed);
        const updated = get(resUpdated, 'data.data', {});
        setCurrentGCC(updated.id);
      } else {
        const resCreated = await createGccDraft(transformed);
        const created = get(resCreated, 'data.data', {});
        if (created) {
          setCurrentGCC(created.id);
        }
      }
      await refetchLatestDraft();
      if (isLastStep) {
        if (type === 'doc') {
          setWorkFlow('signOff');
        } else {
          setWorkFlow('preview');
        }
        if (currentGCC) {
          await refetchDetail();
        }
      }
      notification.success({
        closeIcon: true,
        message: 'Save Draft Succeed',
        placement: 'bottomLeft',
      });
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Save Draft Failed',
      });
    }
  };

  const handlePublish = async (values) => {
    try {
      const { reminder } = values;
      const res = await publish({
        ...ComplianceMappings.transformReminder(reminder),
        publishFileType: exportType.current === 'pdf' ? 'pdf' : 'docx',
      });
      const bodyData = get(res, 'data.data', null);
      if (bodyData) {
        let downloadUrl = '';
        let fileName = '';
        if (exportType.current === 'pdf') {
          downloadUrl = get(bodyData, 'pdf.downloadUrl', null);
          fileName = get(bodyData, 'pdf.fileName', null);
        } else {
          downloadUrl = get(bodyData, 'word.downloadUrl', null);
          fileName = get(bodyData, 'word.fileName', null);
        }
        if (downloadUrl) {
          download(fileName, downloadUrl, true);
        }
        setShowReminder(false);
        notification.success({
          closeIcon: true,
          placement: 'bottomLeft',
          message: 'Download Document Success',
        });
        modal.info({
          icon: null,
          closable: true,
          centered: true,
          title: 'Document Saved',
          content:
            'The generated document has been downloaded to your device and is also available on the Document page for this product.',
          cancelButtonProps: { hidden: true },
          okButtonProps: { hidden: true },
          className: classes.warningModal,
        });
        onConfirmSuccess();
        resetFlow();
      } else {
        throw new Error('Data is empty');
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Download Document Failed',
      });
    }
  };

  const handleSignOff = async (values) => {
    try {
      const { raw, ...signatureValues } = values;
      const { signature, signatureFiles } = signatureValues;
      let uploadedSignature = null;
      if (signatureFiles.length > 0) {
        const signatureFile = signatureFiles[0];
        const { originFileObj } = signatureFile;
        if (originFileObj) {
          const [uploadedFile] = await handleUpload(signatureFiles);
          uploadedSignature = uploadedFile;
        } else {
          uploadedSignature = signatureFile;
        }
      } else {
        const signatureFile = FileHelpers.convertDataUrlToFile(signature, 'signature.png');
        const [uploadedFile] = await handleUpload([{ originFileObj: signatureFile }]);
        uploadedSignature = uploadedFile;
      }
      const form = TRANSFORMER_BY_TYPE.doc({
        ...raw,
        ...raw.general,
      });
      const transformed = TRANSFORMER_BY_TYPE.signOff({
        ...signatureValues,
        signatureFiles: [uploadedSignature],
      });
      const resUpdated = await updateGccDraft({
        ...form,
        signature: transformed,
      });
      const updated = get(resUpdated, 'data.data', {});
      setCurrentGCC(updated.id);
      setWorkFlow('preview');
      refetchLatestDraft();
      refetchDetail();
    } catch (error) {
      notification.error({
        message: 'Sign Off Failed',
      });
      console.error(error);
    }
  };

  const gccCpcInitialValues = React.useMemo(() => {
    const defaultValues = {
      testData: [],
      laboratory: [],
      general: {},
    };
    if (!loaded) return defaultValues;
    try {
      const mapper = MAPPER_BY_TYPE[type];
      const formValues = !latestData ? null : mapper(latestData);

      const generalDefaultValues = GccCpcMappings.mapInitialValuesToForm({
        company: data,
        product,
        supplier: get(product, 'suppliers.data[0]', {}) || {},
      });
      return {
        testData: [],
        laboratory: [],
        notifiedBodyInformation: {},
        general: {
          ...generalDefaultValues.general,
          countryId,
        },
        ...formValues,
      };
    } catch (error) {
      console.error(error);
      return defaultValues;
    }
  }, [product, data, latestData]);

  const signOffInitialValues = React.useMemo(() => {
    if (isEmpty(latestData)) return {};
    const mapper = MAPPER_BY_TYPE.signOff;
    const raw = MAPPER_BY_TYPE.doc(latestData);
    const signature = mapper(latestData || {});
    return {
      ...signature,
      raw,
    };
  }, [latestData]);

  React.useEffect(() => {
    if (!latestData) {
      return;
    }
    if (latestData && !currentGCC) {
      setCurrentGCC(latestData.id);
    }
  }, [latestData]);

  React.useEffect(() => {
    if (['preview', 'signOff'].includes(workFlow) && currentGCC) {
      handleNextStep();
    }
  }, [workFlow, currentGCC]);

  return (
    <>
      <FormGenerateGCCModal
        destroyOnClose
        width={1100}
        maskClosable={false}
        wrapClassName={classes.overlay}
        childRef={formRef}
        type={type}
        initialValues={gccCpcInitialValues}
        testDataResource={{
          name: 'product-compliance-test-data',
          metadata: {
            params: {
              id: productId,
            },
          },
        }}
        complianceDataUrl={`/product/profile/${productId}?sections=compliance_data`}
        countryId={countryId}
        onCancel={() => {
          closeForm(type);
          onFormErrors(null);
        }}
        onOk={(closeModal) => {
          closeForm(type);
          closeModal();
        }}
        onErrors={(formErrors) => {
          onFormErrors(formErrors);
        }}
        onSubmit={(gccValues, isLastStep) => {
          handleSaveData(gccValues, isLastStep);
        }}
        footer={
          <div className={classes.modalFooter}>
            <div>
              <Button
                loading={createDraftLoading || updateDraftLoading || fetchPreviewFileLoading}
                type="primary"
                ghost
                onClick={() => {
                  formRef.current.save();
                }}>
                Save
              </Button>
              <Button
                loading={createDraftLoading || updateDraftLoading || fetchPreviewFileLoading}
                type="primary"
                onClick={() => {
                  const { preview } = formRef.current.next();
                  setShowPreview(preview);
                }}>
                Next
              </Button>
              {isEmpty(errors) ? null : (
                <caption className={classes.error}>{getMessageError()}</caption>
              )}
            </div>
            <span className={classes.captionWrapper}>
              {lastSavedData ? (
                <caption>Last saved: {lastSavedData}</caption>
              ) : (
                <caption>No saved draft</caption>
              )}
            </span>
          </div>
        }
        state={currentGCC ? 'edit' : 'create'}
        {...modalForm}
      />
      <PreviewDocumentModal
        title="Preview"
        closable={false}
        maskClosable={false}
        destroyOnClose
        width={1100}
        open={showPreview}
        loading={fetchPreviewFileLoading}
        tip="Loading document..."
        onCancel={() => {
          resetFlow();
        }}
        onOk={(closeModal) => {
          setShowPreview(false);
          closeModal();
        }}
        type="pdf"
        fileUrl={pdfPreviewUrl}
        wrapClassName={classes.overlay}
        footer={
          <div className={classes.modalFooter}>
            <div>
              <Button
                loading={fetchPreviewFileLoading}
                type="primary"
                ghost
                onClick={() => {
                  if (type !== 'doc') {
                    openForm(type);
                    setShowPreview(false);
                    setWorkFlow('create');
                  } else {
                    setShowPreview(false);
                    setWorkFlow('signOff');
                  }
                }}>
                Back
              </Button>
              <Button
                loading={fetchPreviewFileLoading}
                type="primary"
                onClick={() => {
                  setShowPreview(false);
                  setShowReminder(true);
                  setWorkFlow('publish');
                }}>
                Next
              </Button>
            </div>
          </div>
        }
      />

      <QMASDocumentModal
        open={showSetReminder}
        maskClosable={false}
        destroyOnClose
        width={700}
        hiddenSegmented
        title="Set Dates & Reminder"
        wrapClassName={classes.reminderModal}
        featureFlags={['reminder']}
        files={[]}
        loading={confirmDocumentLoading}
        relatedSource={{}}
        childRef={reminderRef}
        fileAction={{
          shouldShowFileType: false,
        }}
        showDocumentList={false}
        onCancel={() => {
          resetFlow();
        }}
        onSubmit={(values) => {
          handlePublish(values);
        }}
        shouldDisabledField={({ name }) => {
          if (name === 'reminder.issueDate') {
            return true;
          }
          return false;
        }}
        initialValues={{
          ...INITIAL_VALUES,
          reminder: {
            ...INITIAL_VALUES.reminder,
            issueDate: moment(),
          },
          files: [],
        }}
        footer={
          <div className={classes.modalFooter}>
            <div>
              <Button
                loading={confirmDocumentLoading}
                type="primary"
                onClick={() => {
                  exportType.current = 'pdf';
                  reminderRef.current.submit();
                }}>
                Confirm & Download PDF
              </Button>
              <Button
                loading={confirmDocumentLoading}
                type="primary"
                onClick={() => {
                  exportType.current = 'doc';
                  reminderRef.current.submit();
                }}>
                Confirm & Download Word Doc
              </Button>
            </div>
          </div>
        }
      />
      <SignOffFormModal
        width={1100}
        destroyOnClose
        closable={false}
        maskClosable={false}
        title="Sign Off"
        initialValues={signOffInitialValues}
        open={workFlow === 'signOff'}
        childRef={signOffRef}
        wrapClassName={classes.signOffModal}
        onSubmit={handleSignOff}
        footer={
          <div className={classes.modalFooter}>
            <div>
              <Button
                loading={fetchPreviewFileLoading}
                type="primary"
                ghost
                onClick={() => {
                  openForm(type);
                  setShowPreview(false);
                  setWorkFlow('create');
                }}>
                Back
              </Button>
              <Button
                loading={fetchPreviewFileLoading}
                type="primary"
                onClick={() => {
                  signOffRef.current.submit();
                  // setShowPreview(false);
                  // setShowReminder(true);
                  // setWorkFlow('publish');
                }}>
                Next
              </Button>
            </div>
          </div>
        }
      />
      {context}
    </>
  );
};
GCCFormModal.defaultProps = {
  type: 'gcc',
};

GCCFormModal.propTypes = {
  productId: PropTypes.string.isRequired,
  countryId: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  modalForm: PropTypes.object.isRequired,
  openForm: PropTypes.func.isRequired,
  closeForm: PropTypes.func.isRequired,
  onConfirmSuccess: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['gcc', 'cpc', 'doc']),
};

export default GCCFormModal;
